import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { r2Storage } from '../services/r2Storage';

const OnboardingBusinessSetup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const [formData, setFormData] = useState({
    businessName: '',
    fullName: '',
    businessType: '',
    website: '',
    phone: '',
    logo: null,
    logoFile: null // Store the actual file to upload later
  });

  useEffect(() => {
    // Pre-fill with existing data
    if (user?.profile) {
      setFormData(prev => ({
        ...prev,
        businessName: user.profile.business_name || '',
        fullName: user.profile.full_name || ''
      }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be smaller than 2MB', {
        toastId: 'logo-size-error'
      });
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Logo must be a valid image (JPEG, PNG, or WebP)', {
        toastId: 'logo-type-error'
      });
      return;
    }

    // Store file locally and create preview URL - don't upload to Cloudflare yet
    const previewUrl = URL.createObjectURL(file);
    
    setFormData(prev => ({ 
      ...prev, 
      logo: previewUrl, // Preview URL for display
      logoFile: file // Actual file to upload later
    }));
    
    toast.success('Logo selected! It will be uploaded when you complete setup.', {
      toastId: 'logo-selected'
    });
  };

  const handleContinue = async ({ skipLogo = false } = {}) => {
    // Validate required fields
    if (!formData.businessName.trim() || !formData.fullName.trim() || !formData.businessType) {
      toast.error('Please fill in all required fields', {
        toastId: 'validation-error'
      });
      return;
    }

    setIsLoading(true);
    try {
      let logoUrl = null;
      if (formData.logoFile && !skipLogo) {
        try {
          const uploadResult = await r2Storage.uploadLogo(formData.logoFile);
          logoUrl = uploadResult.url;
        } catch (logoError) {
          console.warn('Logo upload failed during business setup:', logoError);
          toast.warn('Logo upload failed. You can upload it later in Profile Settings.', {
            toastId: 'logo-upload-warning'
          });
        }
      }
      sessionStorage.removeItem('pendingLogoUpload');

      await authService.updateProfile({
        business_name: formData.businessName.trim(),
        full_name: formData.fullName.trim(),
        onboarding_step: 'complete',
        onboarding_completed: true,
        trial_setup_completed: true,
        plan_type: 'free',
        subscription_status: 'free',
        free_quotes_limit: 10,
        business_details: {
          business_type: formData.businessType,
          website: formData.website.trim(),
          phone: formData.phone.trim()
        },
        ...(logoUrl ? { avatar_url: logoUrl } : {})
      });

      toast.success('Welcome to QuoteTier!', {
        toastId: 'profile-saved'
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Save business profile error:', error);
      toast.error('Failed to save business profile. Please try again.', {
        toastId: 'profile-save-error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipLogo = () => {
    if (!formData.businessName.trim() || !formData.fullName.trim() || !formData.businessType) {
      toast.error('Please fill in all required fields', {
        toastId: 'validation-error-skip'
      });
      return;
    }
    
    console.log('💾 Skipping logo upload, proceeding with business setup...');
    
    // Clear any selected logo and continue
    setFormData(prev => ({ ...prev, logo: null, logoFile: null }));
    handleContinue({ skipLogo: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-3 text-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            <span className="font-instrument text-3xl">QuoteTier</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="max-w-2xl mx-auto">
          {/* Progress Indicator */}
          <div className="mb-12">
            <div className="flex items-center justify-center space-x-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-medium">✓</div>
                <span className="font-jetbrains ml-2 text-sm text-green-600">Welcome</span>
              </div>
              <div className="w-8 h-px bg-gray-300"></div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm font-medium">2</div>
                <span className="font-jetbrains ml-2 text-sm text-gray-900">Business Setup</span>
              </div>
              <div className="w-8 h-px bg-gray-300"></div>
              <div className="flex items-center">
                <div className="w-8 h-8 border-2 border-gray-300 text-gray-400 rounded-full flex items-center justify-center text-sm font-medium">3</div>
                <span className="font-jetbrains ml-2 text-sm text-gray-400">Trial Setup</span>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="font-instrument text-4xl sm:text-5xl tracking-tight leading-tight mb-4">
              Set up your <span className="text-primary font-bold">business profile</span>
            </h1>
            <p className="font-jetbrains text-lg text-gray-600">
              This information will appear on your quotes to build trust with clients
            </p>
          </div>

          {/* Form */}
          <div className="border border-black rounded-lg p-8 bg-white">
            <form className="space-y-6">
              {/* Logo Upload */}
              <div className="text-center">
                <label className="block font-instrument text-lg font-medium text-gray-900 mb-4">
                  Business Logo
                </label>
                <div className="flex flex-col items-center">
                  {formData.logo ? (
                    <div className="relative">
                      <img 
                        src={formData.logo} 
                        alt="Business logo" 
                        className="w-24 h-24 object-contain rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          // Clean up preview URL if it's a blob
                          if (formData.logo && formData.logo.startsWith('blob:')) {
                            URL.revokeObjectURL(formData.logo);
                          }
                          setFormData(prev => ({ ...prev, logo: null, logoFile: null }));
                        }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    id="logo"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <label
                    htmlFor="logo"
                    className="mt-4 font-jetbrains cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    {uploadingLogo ? (
                      <>
                        <div className="spinner spinner-sm mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        {formData.logo ? 'Change Logo' : 'Upload Logo'}
                      </>
                    )}
                  </label>
                  <p className="font-jetbrains text-xs text-gray-500 mt-2">
                    PNG, JPG, or WebP up to 2MB (Optional)<br/>
                    Logo will be uploaded when you complete setup
                  </p>
                </div>
              </div>

              {/* Business Details */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="businessName" className="block font-instrument text-lg font-medium text-gray-900 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    className="font-jetbrains w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                    placeholder="Enter your business name"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="fullName" className="block font-instrument text-lg font-medium text-gray-900 mb-2">
                    Your Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="font-jetbrains w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="businessType" className="block font-instrument text-lg font-medium text-gray-900 mb-2">
                  Business Type *
                </label>
                <select
                  id="businessType"
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleInputChange}
                  className="font-jetbrains w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                  required
                >
                  <option value="">Select your business type</option>
                  <option value="consulting">Consulting & Professional Services</option>
                  <option value="marketing">Marketing & Advertising</option>
                  <option value="construction">Construction & Contracting</option>
                  <option value="home-services">Home Services (HVAC, Plumbing, etc.)</option>
                  <option value="creative">Creative & Design</option>
                  <option value="technology">Technology & Software</option>
                  <option value="events">Events & Wedding Services</option>
                  <option value="retail">Retail & E-commerce</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="website" className="block font-instrument text-lg font-medium text-gray-900 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="font-jetbrains w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                    placeholder="https://yourwebsite.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block font-instrument text-lg font-medium text-gray-900 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="font-jetbrains w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </form>

            {/* Actions */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-between">
              <button
                type="button"
                onClick={handleSkipLogo}
                className="font-jetbrains px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Continue Without Logo
              </button>
              
              <button
                type="button"
                onClick={handleContinue}
                disabled={isLoading || uploadingLogo}
                className="font-instrument text-lg px-8 py-3 bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="spinner spinner-sm text-white border-white/30 mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  'Continue to Trial Setup'
                )}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OnboardingBusinessSetup;
